import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Plus, Download, Search, CheckCircle, Clock, Pencil, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR, getUser } from '@/lib/auth';
import api from '@/lib/api';

const ASSET_TYPES = ['Laptop', 'Mobile', 'SIM Card', 'ID Card', 'Desktop', 'Monitor', 'Keyboard', 'Mouse', 'Headset', 'Charger', 'Bag', 'Uniform', 'Key/Access Card', 'Other'];
const CONDITIONS = ['New', 'Good', 'Fair', 'Refurbished'];

function canManageAssets() {
  const user = getUser();
  return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'hr';
}

export default function AssetsPage() {
  const manage = canManageAssets();
  if (!manage) return <EmployeeAssetsView />;
  return <AdminAssetsView />;
}


function AdminAssetsView() {
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [form, setForm] = useState({ employee_id: '', asset_name: '', asset_type: '', serial_number: '', condition: 'New', issued_date: new Date().toISOString().split('T')[0], notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([
        api.get('/api/assets'),
        api.get('/api/employees?limit=200')
      ]);
      setAssets(a.data);
      setEmployees(e.data.employees || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return assets.filter(a => {
      const matchSearch = !search || a.employee_name?.toLowerCase().includes(search.toLowerCase()) || a.asset_name?.toLowerCase().includes(search.toLowerCase()) || a.serial_number?.toLowerCase().includes(search.toLowerCase());
      const matchType = typeFilter === 'All' || a.asset_type === typeFilter;
      return matchSearch && matchType;
    });
  }, [assets, search, typeFilter]);

  const handleAdd = async () => {
    if (!form.employee_id || !form.asset_name || !form.asset_type) {
      toast.error('Fill employee, asset name, and type'); return;
    }
    try {
      await api.post('/api/assets', form);
      toast.success('Asset assigned');
      setAddOpen(false);
      setForm({ employee_id: '', asset_name: '', asset_type: '', serial_number: '', condition: 'New', issued_date: new Date().toISOString().split('T')[0], notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = async () => {
    if (!editAsset) return;
    try {
      await api.put(`/api/assets/${editAsset.id}`, {
        asset_name: editAsset.asset_name,
        asset_type: editAsset.asset_type,
        serial_number: editAsset.serial_number,
        condition: editAsset.condition,
        notes: editAsset.notes,
      });
      toast.success('Asset updated');
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await api.delete(`/api/assets/${id}`);
      toast.success('Asset deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleDownloadPdf = async (assetId) => {
    try {
      const r = await api.get(`/api/assets/${assetId}/acceptance-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'asset_acceptance.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Acceptance letter downloaded');
    } catch (e) { toast.error('Failed to download'); }
  };

  // Stats
  const totalAssets = assets.length;
  const acknowledged = assets.filter(a => a.acknowledged).length;
  const pending = totalAssets - acknowledged;
  const uniqueTypes = [...new Set(assets.map(a => a.asset_type))];

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// ASSETS</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Asset Allocation</h1>
          <p className="text-sm text-slate-500">Track and manage company assets assigned to employees</p>
        </div>
        <Button className="bg-[#0D2850] hover:bg-[#0B2246] gap-1" onClick={() => setAddOpen(true)} data-testid="assign-asset-btn">
          <Plus className="w-4 h-4" /> Assign Asset
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Assets', value: totalAssets, icon: Package, bg: 'bg-blue-50', color: 'text-[#0D2850]' },
          { label: 'Acknowledged', value: acknowledged, icon: CheckCircle, bg: 'bg-emerald-50', color: 'text-emerald-600' },
          { label: 'Pending Ack.', value: pending, icon: Clock, bg: 'bg-amber-50', color: 'text-amber-600' },
          { label: 'Asset Types', value: uniqueTypes.length, icon: FileText, bg: 'bg-slate-50', color: 'text-slate-600' },
        ].map((s, i) => (
          <Card key={i} className="border-slate-200">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className="text-xl font-semibold font-tabular">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by employee, asset, serial..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="asset-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] h-9" data-testid="asset-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Types</SelectItem>
            {ASSET_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Assets Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">{assets.length === 0 ? 'No assets assigned yet' : 'No matching assets'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Employee</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Serial/ID</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(a => (
                    <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                      <TableCell>
                        <p className="text-sm font-medium">{a.employee_name}</p>
                        <p className="text-xs text-slate-400">{a.employee_code}</p>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{a.asset_name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{a.asset_type}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600">{a.serial_number || '-'}</TableCell>
                      <TableCell className="text-sm">{a.condition}</TableCell>
                      <TableCell className="text-sm">{a.issued_date}</TableCell>
                      <TableCell>
                        {a.acknowledged ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">Acknowledged</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadPdf(a.id)} title="Download Acceptance Letter" data-testid={`download-acceptance-${a.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditAsset({...a}); setEditOpen(true); }} data-testid={`edit-asset-${a.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(a.id)} data-testid={`delete-asset-${a.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Asset Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Assign Asset</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={form.employee_id} onValueChange={v => setForm({...form, employee_id: v})}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Asset Name</Label><Input value={form.asset_name} onChange={e => setForm({...form, asset_name: e.target.value})} placeholder="e.g. Dell Latitude 5520" data-testid="assign-asset-name" /></div>
              <div>
                <Label className="text-xs">Asset Type</Label>
                <Select value={form.asset_type} onValueChange={v => setForm({...form, asset_type: v})}>
                  <SelectTrigger data-testid="assign-asset-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>{ASSET_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Serial Number</Label><Input value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})} placeholder="Optional" data-testid="assign-serial" /></div>
              <div>
                <Label className="text-xs">Condition</Label>
                <Select value={form.condition} onValueChange={v => setForm({...form, condition: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Date Issued</Label>
              <Input type="date" value={form.issued_date} onChange={e => setForm({...form, issued_date: e.target.value})} data-testid="assign-date" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" data-testid="assign-notes" />
            </div>
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleAdd} data-testid="submit-assign-asset">Assign Asset</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Asset</DialogTitle></DialogHeader>
          {editAsset && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Assigned to: <span className="font-medium text-slate-700">{editAsset.employee_name}</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Asset Name</Label><Input value={editAsset.asset_name} onChange={e => setEditAsset({...editAsset, asset_name: e.target.value})} data-testid="edit-asset-name" /></div>
                <div>
                  <Label className="text-xs">Asset Type</Label>
                  <Select value={editAsset.asset_type} onValueChange={v => setEditAsset({...editAsset, asset_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSET_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Serial Number</Label><Input value={editAsset.serial_number} onChange={e => setEditAsset({...editAsset, serial_number: e.target.value})} data-testid="edit-serial" /></div>
                <div>
                  <Label className="text-xs">Condition</Label>
                  <Select value={editAsset.condition} onValueChange={v => setEditAsset({...editAsset, condition: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Notes</Label><Input value={editAsset.notes || ''} onChange={e => setEditAsset({...editAsset, notes: e.target.value})} data-testid="edit-asset-notes" /></div>
              <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleEdit} data-testid="save-edit-asset">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EmployeeAssetsView() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/assets/my-assets').then(r => {
      setAssets(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleAcknowledge = async (id) => {
    try {
      await api.post(`/api/assets/${id}/acknowledge`);
      toast.success('Asset acknowledged');
      const { data } = await api.get('/api/assets/my-assets');
      setAssets(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const r = await api.get(`/api/assets/${id}/acceptance-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'asset_acceptance.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download'); }
  };

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div>
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// MY ASSETS</Badge>
        <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">My Assets</h1>
        <p className="text-sm text-slate-500">Company assets assigned to you</p>
      </div>

      {loading ? <Skeleton className="h-40" /> : assets.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No assets assigned to you</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {assets.map(a => (
            <Card key={a.id} className="border-slate-200" data-testid={`my-asset-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0D2850]/5 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-[#0D2850]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#0B1B33]">{a.asset_name}</p>
                      <p className="text-xs text-slate-500">{a.asset_type} {a.serial_number ? `| ${a.serial_number}` : ''}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Issued: {a.issued_date} | Condition: {a.condition}</p>
                      {a.notes && <p className="text-xs text-slate-400 mt-0.5">{a.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.acknowledged ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs gap-1">
                        <CheckCircle className="w-3 h-3" /> Acknowledged
                      </Badge>
                    ) : (
                      <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246] text-xs gap-1" onClick={() => handleAcknowledge(a.id)} data-testid={`acknowledge-${a.id}`}>
                        <CheckCircle className="w-3 h-3" /> Acknowledge
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => handleDownloadPdf(a.id)} data-testid={`my-download-acceptance-${a.id}`}>
                      <Download className="w-3 h-3" /> Letter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
